import { FocusEvent, ReactNode, useCallback, useId, useState } from "react";
import { AccordionRoot } from "@design-system/Accordion/Accordion";
import { Button } from "@design-system/Button/Button";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { VariableTextField } from "@components/forms/VariableTextField/VariableTextField";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import { REPORT_VARIABLES } from "@common/helpers/report-variables";
import { ReportSettingsSpotExplanation } from "@common/helpers/report-settings/explain-report-setting-spot";
import { TemplateNames } from "../reportSettingsSourceCopy";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { LayoutValues } from "@common/schemas/domains/reportSettings/layout-setting.schema";
import {
    REPORT_SETTINGS_WATERMARK_MAX_LENGTH,
    ReportSettingLogoOption,
    ReportSettingRowVisibility,
} from "@common/types/reports/report-settings";
import {
    REPORT_SETTINGS_DEFAULT_REPORT_KEY,
    ReportSettingsConcreteReportKey,
} from "@common/types/reports/report-settings-report-key";
import invoiceStyles from "@components/invoices/NativeInvoiceDrawer/NativeInvoiceDrawer.module.scss";
import { PanelSection } from "@components/invoices/NativeInvoiceDrawer/PanelSection";
import { NumberFormatSection } from "../numberFormat/NumberFormatSection";
import { PreviewSplit } from "../PreviewSplit";
import {
    PreviewHighlightTarget,
    ReportPreview,
} from "../preview/ReportPreview";
import { ResetToInheritedButton } from "../ResetToInheritedButton";
import styles from "../ReportSettings.module.scss";
import {
    BRANDING_TEXT_FIELDS,
    LayoutTextFieldKey,
    TextFieldConfig,
    WATERMARK_FIELD,
    getRowVisibilityOptions,
} from "./layoutControls";
import {
    ReportSettingsDraftSpot,
    UpdateReportSetting,
} from "./reportSettingsDrafts";

export type Section = "layout" | "export" | "numberFormat";

const combineLogoOption = (params: {
    hasFirmLogo: boolean;
    hasEntityLogo: boolean;
}): ReportSettingLogoOption => {
    if (params.hasFirmLogo && params.hasEntityLogo) {
        return ReportSettingLogoOption.BOTH;
    }
    if (params.hasFirmLogo) {
        return ReportSettingLogoOption.FIRM;
    }
    if (params.hasEntityLogo) {
        return ReportSettingLogoOption.ENTITY;
    }
    return ReportSettingLogoOption.NONE;
};

interface Props {
    spot: ReportSettingsDraftSpot;
    templateNames: TemplateNames;
    view: ReportSettingsSpotExplanation;
    previewLayout: LayoutValues;
    previewNumberFormat: NumberFormatValues;
    previewReportKey: ReportSettingsConcreteReportKey;
    previewPicker: ReactNode;
    canEdit: boolean;
    isSaving: boolean;
    isOpen: boolean;
    topControls: ReactNode;
    openSection: Section | null;
    onOpenSectionChange: (section: Section | null) => void;
    updateField: UpdateReportSetting;
    onSaveAndClose: () => void;
    onCancel: () => void;
    entityName: string | null;
    firmName: string | null;
    firmLogoUrl: string | null;
    entityLogoUrl: string | null;
}

const isHighlightTarget = (value: string): value is PreviewHighlightTarget =>
    BRANDING_TEXT_FIELDS.some((field) => field.key === value);

export const LayoutEditorForm = ({
    spot,
    templateNames,
    view,
    previewLayout,
    previewNumberFormat,
    previewReportKey,
    previewPicker,
    canEdit,
    isSaving,
    isOpen,
    topControls,
    openSection,
    onOpenSectionChange,
    updateField,
    onSaveAndClose,
    onCancel,
    entityName,
    firmName,
    firmLogoUrl,
    entityLogoUrl,
}: Props) => {
    const rowVisibilityLabelId = useId();
    const [fieldRevisions, setFieldRevisions] = useState<
        Partial<Record<LayoutTextFieldKey, number>>
    >({});
    const [highlight, setHighlight] = useState<PreviewHighlightTarget | null>(
        null,
    );

    const handleSectionFocus = useCallback(
        (event: FocusEvent<HTMLDivElement>) => {
            const target = event.target.closest("[data-preview-target]");
            const value = target?.getAttribute("data-preview-target");
            if (value && isHighlightTarget(value)) {
                setHighlight(value);
            }
        },
        [],
    );

    const handleSectionBlur = useCallback(
        (event: FocusEvent<HTMLDivElement>) => {
            const next = event.relatedTarget;
            if (
                !(next instanceof Node) ||
                !event.currentTarget.contains(next)
            ) {
                setHighlight(null);
            }
        },
        [],
    );

    const resetTextField = useCallback(
        (key: LayoutTextFieldKey) => {
            updateField(key, null);
            setFieldRevisions((prev) => ({
                ...prev,
                [key]: (prev[key] ?? 0) + 1,
            }));
        },
        [updateField],
    );

    const renderTextField = (config: TextFieldConfig) => {
        const field = view[config.key];
        return (
            <VariableTextField
                key={`${config.key}-${fieldRevisions[config.key] ?? 0}`}
                label={config.label}
                optional={config.isOptional}
                labelRight={
                    <div className={styles.resetSlot}>
                        <ResetToInheritedButton
                            field={config.key}
                            explanation={field}
                            templateNames={templateNames}
                            canEdit={canEdit}
                            onReset={() => resetTextField(config.key)}
                        />
                    </div>
                }
                value={field.effective.value}
                onChange={(next) => updateField(config.key, next)}
                variables={REPORT_VARIABLES}
                placeholder={config.placeholder}
                disabled={!canEdit}
                size="lg"
            />
        );
    };

    const logoValue = view.logoOption.effective.value;
    const hasFirmLogo =
        logoValue === ReportSettingLogoOption.FIRM ||
        logoValue === ReportSettingLogoOption.BOTH;
    const hasEntityLogo =
        logoValue === ReportSettingLogoOption.ENTITY ||
        logoValue === ReportSettingLogoOption.BOTH;

    const { rowVisibility } = view;
    const { reportKey } = spot;
    const isReportDefaultRowVisibilityUnset =
        reportKey === REPORT_SETTINGS_DEFAULT_REPORT_KEY &&
        rowVisibility?.effective.source.kind === "kick-default";

    const editorContent = (
        <>
            {topControls}
            <AccordionRoot<Section>
                value={openSection ? [openSection] : []}
                onValueChange={(sections) =>
                    onOpenSectionChange(sections[0] ?? null)
                }
                className={styles.editorSections}
            >
                <PanelSection label="Layout" value="layout">
                    <VStack
                        gap={16}
                        onFocusCapture={handleSectionFocus}
                        onBlurCapture={handleSectionBlur}
                    >
                        {BRANDING_TEXT_FIELDS.map((config) => (
                            <div
                                key={config.key}
                                data-preview-target={config.key}
                            >
                                {renderTextField(config)}
                            </div>
                        ))}
                        {rowVisibility && (
                            <Field.Root>
                                <HStack justify="between" align="end" gap={8}>
                                    <Field.Label
                                        id={rowVisibilityLabelId}
                                        nativeLabel={false}
                                        render={<span />}
                                    >
                                        <Tooltip content="Choose which accounts appear as rows in the report.">
                                            <span>Account visibility</span>
                                        </Tooltip>
                                    </Field.Label>
                                    <div className={styles.resetSlot}>
                                        <ResetToInheritedButton
                                            field="rowVisibility"
                                            explanation={rowVisibility}
                                            templateNames={templateNames}
                                            canEdit={canEdit}
                                            onReset={() =>
                                                updateField(
                                                    "rowVisibility",
                                                    null,
                                                )
                                            }
                                        />
                                    </div>
                                </HStack>
                                <ToggleGroup.Root<ReportSettingRowVisibility>
                                    className={styles.fullWidthToggleGroup}
                                    value={
                                        isReportDefaultRowVisibilityUnset
                                            ? null
                                            : rowVisibility.effective.value
                                    }
                                    onValueChange={(value) =>
                                        value &&
                                        updateField("rowVisibility", value)
                                    }
                                    size="lg"
                                    disabled={!canEdit}
                                    aria-labelledby={rowVisibilityLabelId}
                                >
                                    {getRowVisibilityOptions(reportKey).map(
                                        (option) => (
                                            <Tooltip
                                                key={option.value}
                                                content={option.tooltip}
                                            >
                                                <ToggleGroup.Item
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </ToggleGroup.Item>
                                            </Tooltip>
                                        ),
                                    )}
                                </ToggleGroup.Root>
                            </Field.Root>
                        )}
                    </VStack>
                </PanelSection>

                <PanelSection label="Formatting" value="numberFormat">
                    <NumberFormatSection
                        view={view}
                        templateNames={templateNames}
                        canEdit={canEdit}
                        updateField={updateField}
                    />
                </PanelSection>

                <PanelSection label="PDF export" value="export">
                    <VStack gap={4}>
                        <HStack justify="between" align="end" gap={8}>
                            <span className={styles.sectionLabel}>Logo</span>
                            <div className={styles.resetSlot}>
                                <ResetToInheritedButton
                                    field="logoOption"
                                    explanation={view.logoOption}
                                    templateNames={templateNames}
                                    canEdit={canEdit}
                                    onReset={() =>
                                        updateField("logoOption", null)
                                    }
                                />
                            </div>
                        </HStack>
                        <HStack gap={16} align="center">
                            <Checkbox
                                checked={hasFirmLogo}
                                label="Firm logo"
                                disabled={!canEdit}
                                onCheckedChange={(checked) =>
                                    updateField(
                                        "logoOption",
                                        combineLogoOption({
                                            hasFirmLogo: checked,
                                            hasEntityLogo,
                                        }),
                                    )
                                }
                            />
                            <Checkbox
                                checked={hasEntityLogo}
                                label="Entity logo"
                                disabled={!canEdit}
                                onCheckedChange={(checked) =>
                                    updateField(
                                        "logoOption",
                                        combineLogoOption({
                                            hasFirmLogo,
                                            hasEntityLogo: checked,
                                        }),
                                    )
                                }
                            />
                        </HStack>
                    </VStack>
                    <Field.Root>
                        <HStack justify="between" align="end" gap={8}>
                            <Field.Label>Watermark</Field.Label>
                            <div className={styles.resetSlot}>
                                <ResetToInheritedButton
                                    field="watermarkText"
                                    explanation={view.watermarkText}
                                    templateNames={templateNames}
                                    canEdit={canEdit}
                                    onReset={() =>
                                        updateField("watermarkText", null)
                                    }
                                />
                            </div>
                        </HStack>
                        <Input
                            value={view.watermarkText.effective.value}
                            onChange={(event) =>
                                updateField("watermarkText", event.target.value)
                            }
                            maxLength={REPORT_SETTINGS_WATERMARK_MAX_LENGTH}
                            placeholder={WATERMARK_FIELD.placeholder}
                            disabled={!canEdit}
                            size="xl"
                            data-testid="watermark-field-input"
                        />
                    </Field.Root>
                </PanelSection>
            </AccordionRoot>
        </>
    );

    return (
        <PreviewSplit
            left={editorContent}
            footer={
                <CommandIsland className={invoiceStyles.commandBar}>
                    <div className={invoiceStyles.commandBarGroup} />
                    <div className={invoiceStyles.commandBarGroup}>
                        <Button
                            variant="secondary"
                            size="md"
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                        <ButtonWithLoader
                            variant="primary"
                            size="md"
                            loading={isSaving}
                            onClick={onSaveAndClose}
                            hotKey="mod+enter"
                            hotKeyDisplayPosition="inline"
                            hotKeyOptions={{
                                description: "Save report settings",
                                enabled: isOpen && !isSaving,
                                preventDefault: true,
                                enableOnFormTags: true,
                            }}
                        >
                            Save
                        </ButtonWithLoader>
                    </div>
                </CommandIsland>
            }
            right={
                <VStack
                    gap={12}
                    align="center"
                    className={styles.previewColumn}
                >
                    {previewPicker}
                    <ReportPreview
                        layout={previewLayout}
                        numberFormat={previewNumberFormat}
                        reportType={previewReportKey}
                        highlight={highlight}
                        entityName={entityName}
                        firmName={firmName}
                        firmLogoUrl={firmLogoUrl}
                        entityLogoUrl={entityLogoUrl}
                    />
                </VStack>
            }
        />
    );
};

LayoutEditorForm.displayName = "LayoutEditorForm";
